import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../style.css';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    
    // Avatar moods for animation
    const [avatarMood, setAvatarMood] = useState('playing');

    const characters = [
        { 
            id: 1, 
            name: 'Bubbles', 
            color: '#b8e0d2', 
            accent: '#a3d5d3', 
            hair: '#8B4513' 
        },
        { 
            id: 2, 
            name: 'Pippin', 
            color: '#ffd6e0', 
            accent: '#ffb3c8', 
            hair: '#FF69B4' 
        },
        { 
            id: 3, 
            name: 'Sunny', 
            color: '#fff9c4', 
            accent: '#fff176', 
            hair: '#FFD700' 
        },
        { 
            id: 4, 
            name: 'Mochi', 
            color: '#c8e6c9', 
            accent: '#a5d6a7', 
            hair: '#4CAF50' 
        }
    ];

    // Set default playing state
    useEffect(() => {
        if (avatarMood === 'neutral') {
            setAvatarMood('playing');
        }
    }, [avatarMood]);

    const handleEmailFocus = () => {
        setAvatarMood('look');
    };

    const handleInputBlur = () => {
        if (avatarMood === 'look') {
            setTimeout(() => setAvatarMood('playing'), 500);
        }
    };

    const handleCharacterHover = (characterName) => {
        toast.info(`${characterName} is here to help! 📧`, {
            position: "top-center",
            autoClose: 2000,
            hideProgressBar: true,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error('Please enter a valid email address', {
                position: "top-center",
            });
            setAvatarMood('shocked');
            setTimeout(() => setAvatarMood('playing'), 2000);
            return;
        }

        setLoading(true);
        setAvatarMood('thinking');

        try {
            console.log('Sending password reset request for:', email);
            
            const response = await fetch('/api/verifyemail', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email: email.toLowerCase().trim() })
            });

            const data = await response.json();
            console.log('Response:', data);

            if (!response.ok) {
                throw new Error(data.message || 'Failed to send reset link');
            }

            // Success
            setAvatarMood('happy');
            setEmailSent(true);
            toast.success('Password reset link sent! Check your email inbox.', {
                position: "top-center",
                autoClose: 5000,
            });
            
            setEmail(''); // Clear the form

        } catch (error) {
            console.error('Error sending reset link:', error);
            setAvatarMood('sad');
            
            toast.error(error.message || 'Failed to send reset link. Please try again.', {
                position: "top-center",
                autoClose: 5000,
            });
        } finally {
            setLoading(false);
            setTimeout(() => setAvatarMood('playing'), 2000);
        }
    };

    return (
        <div className="logbody">
            <div className="split-container">
                {/* Left Side - Character Group */}
                <div className="animation-side">
                    <div className="sun"></div>
                    <div className="landscape-container">
                        <div className="grass-landscape">
                            <div className="grass-blades"></div>
                            <div className="character-group">
                                {characters.map((character, index) => (
                                    <div 
                                        key={character.id}
                                        className={`character-wrapper character-${index + 1}`}
                                    >
                                        <div 
                                            className={`avatar ${avatarMood}`}
                                            style={{
                                                '--head-color': character.color,
                                                '--body-color': character.accent,
                                                '--hair-color': character.hair
                                            }}
                                            onMouseEnter={() => handleCharacterHover(character.name)}
                                            title={`${character.name} will help you recover your password!`}
                                        >
                                            <div className="head">
                                                <div className="face">
                                                    <div className="eyes">
                                                        <div className="eye"></div>
                                                        <div className="eye"></div>
                                                    </div>
                                                    <div className="cheeks"></div>
                                                    <div className="mouth"></div>
                                                </div>
                                            </div>
                                            <div className="arms">
                                                <div className="arm left-arm"></div>
                                                <div className="arm right-arm"></div>
                                            </div>
                                            <div className="body">
                                                <div className="decoration"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Forgot Password Form */}
                <div className="form-side">
                    <div className="login-box">
                        <div className="login-header">
                            <h1>UXAAH</h1>
                            <header>Reset Your Password</header>
                            <p className="form-subtitle" style={{marginTop: '0.5rem', fontSize: '0.9rem', color: '#666'}}>
                                {emailSent 
                                    ? "Check your email for the reset link!" 
                                    : "Enter your email and we'll send you a reset link"
                                }
                            </p>
                        </div>
                        
                        {!emailSent ? (
                            <form onSubmit={handleSubmit}>
                                <div className="input-box">
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className="input-field"
                                        placeholder="Enter your email address"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setAvatarMood('look');
                                        }}
                                        onFocus={handleEmailFocus}
                                        onBlur={handleInputBlur}
                                        disabled={loading}
                                    />
                                </div>

                                <div className="info-box" style={{
                                    margin: '1rem 0',
                                    padding: '0.75rem',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: '8px',
                                    fontSize: '0.85rem',
                                    color: '#1976d2',
                                    borderLeft: '4px solid #2196f3'
                                }}>
                                    <p style={{margin: 0}}>
                                        <strong>📧 Note:</strong> The reset link expires in 15 minutes. 
                                        Check your spam folder if you don't see the email.
                                    </p>
                                </div>

                                <div className="input-submit">
                                    <button 
                                        type="submit" 
                                        className="submit-btn" 
                                        disabled={loading}
                                    >
                                        {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="success-message" style={{textAlign: 'center', margin: '2rem 0'}}>
                                <div style={{
                                    fontSize: '4rem',
                                    marginBottom: '1rem'
                                }}>
                                    ✅
                                </div>
                                <p style={{fontSize: '1.1rem', color: '#2e7d32', fontWeight: '600'}}>
                                    Email Sent Successfully!
                                </p>
                                <p style={{fontSize: '0.9rem', color: '#666', marginTop: '1rem'}}>
                                    We've sent a password reset link to your email.
                                    Click the link in the email to reset your password.
                                </p>
                                <div style={{marginTop: '1.5rem'}}>
                                    <button 
                                        onClick={() => setEmailSent(false)}
                                        className="submit-btn"
                                        style={{
                                            backgroundColor: '#6c757d',
                                            padding: '0.75rem 1.5rem'
                                        }}
                                    >
                                        Send Another Link
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        <div className="sign-up-link">
                            <p>
                                Remember your password?{' '}
                                <a href="/login">Back to Login</a>
                            </p>
                            <p style={{marginTop: '0.5rem'}}>
                                Don't have an account?{' '}
                                <a href="/signup">Sign Up</a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer 
                position="top-center" 
                autoClose={5000} 
                theme="light" 
                pauseOnHover
                limit={3}
            />
        </div>
    );
};

export default ForgotPassword;